import { Suspense, lazy } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { Layout } from '@/components/Layout'
import { ProtectedRoute } from '@/components/ProtectedRoute'
import type { UserRole } from '@/lib/api'

const Login = lazy(() => import('@/routes/Login'))
const SetPassword = lazy(() => import('@/routes/SetPassword'))
const ForgotPassword = lazy(() => import('@/routes/ForgotPassword'))
const Dashboard = lazy(() => import('@/routes/Dashboard'))

const Requests = lazy(() => import('@/routes/Requests'))
const RequestDetail = lazy(() => import('@/routes/RequestDetail'))
const Employees = lazy(() => import('@/routes/Employees'))
const EmployeeDetail = lazy(() => import('@/routes/EmployeeDetail'))
const Team = lazy(() => import('@/routes/Team'))
const CalendarPage = lazy(() => import('@/routes/Calendar'))
const MeetingRooms = lazy(() => import('@/routes/MeetingRooms'))
const Incidents = lazy(() => import('@/routes/Incidents'))
const Trainings = lazy(() => import('@/routes/Trainings'))
const Travel = lazy(() => import('@/routes/Travel'))
const Tasks = lazy(() => import('@/routes/Tasks'))
const Settings = lazy(() => import('@/routes/Settings'))
const Reports = lazy(() => import('@/routes/Reports'))
const Approvals = lazy(() => import('@/routes/Approvals'))
const AdminDepartments = lazy(() => import('@/routes/AdminDepartments'))
const AdminUsers = lazy(() => import('@/routes/AdminUsers'))
const Recruitment = lazy(() => import('@/routes/Recruitment'))
const Onboarding = lazy(() => import('@/routes/Onboarding'))
const MyOnboarding = lazy(() => import('@/routes/MyOnboarding'))
const Assets = lazy(() => import('@/routes/Assets'))
const MyAssets = lazy(() => import('@/routes/MyAssets'))
const AssetAssignment = lazy(() => import('@/routes/AssetAssignment'))
const ChangePassword = lazy(() => import('@/routes/ChangePassword'))
const Vehicles = lazy(() => import('@/routes/Vehicles'))

const STAFF: UserRole[] = ['admin', 'manager', 'handler']

function PageLoader() {
  return (
    <div className="flex h-full min-h-[400px] items-center justify-center">
      <div className="h-6 w-6 animate-spin rounded-full border-2 border-wegen-600 border-t-transparent" />
    </div>
  )
}

export default function App() {
  return (
    <ErrorBoundary>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/set-password" element={<SetPassword />} />
          <Route path="/reset-password" element={<SetPassword />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />

          <Route
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/requests" element={<Requests />} />
            <Route path="/requests/:id" element={<RequestDetail />} />
            <Route path="/employees" element={<Employees />} />
            <Route path="/employees/:id" element={<EmployeeDetail />} />
            <Route path="/team" element={<Team />} />
            <Route path="/calendar" element={<CalendarPage />} />
            <Route path="/meeting-rooms" element={<MeetingRooms />} />
            <Route path="/incidents" element={<Incidents />} />
            <Route path="/trainings" element={<Trainings />} />
            <Route path="/travel" element={<Travel />} />
            <Route path="/tasks" element={<Tasks />} />
            <Route path="/reports" element={<Reports />} />
            <Route path="/approvals" element={<Approvals />} />
            <Route path="/departments" element={<AdminDepartments />} />
            <Route path="/users" element={<AdminUsers />} />
            <Route path="/recruitment" element={<ProtectedRoute requireRole={STAFF} allowDeptHead><Recruitment /></ProtectedRoute>} />
            <Route path="/recruitment/:requisitionId" element={<ProtectedRoute requireRole={STAFF} allowDeptHead><Recruitment /></ProtectedRoute>} />
            <Route path="/onboarding" element={<ProtectedRoute requireRole={STAFF}><Onboarding /></ProtectedRoute>} />
            <Route path="/onboarding/:trackerId" element={<ProtectedRoute requireRole={STAFF}><Onboarding /></ProtectedRoute>} />
            <Route path="/my-onboarding" element={<MyOnboarding />} />
            <Route path="/assets" element={<ProtectedRoute requireRole={STAFF}><Assets /></ProtectedRoute>} />
            <Route path="/assets/mine" element={<MyAssets />} />
            <Route path="/assets/assignments/:id" element={<AssetAssignment />} />
            <Route path="/vehicles" element={<Vehicles />} />
            <Route path="/account/password" element={<ChangePassword />} />
            <Route path="/settings" element={<Settings />} />
          </Route>

          <Route path="/" element={<Navigate to="/dashboard" replace />} />

          <Route
            path="*"
            element={
              <div className="flex min-h-screen flex-col items-center justify-center px-4 text-center">
                <span className="text-6xl font-bold text-slate-300">404</span>
                <p className="mt-3 text-lg font-semibold text-slate-700">Page not found</p>
                <a href="/dashboard" className="mt-4 text-sm text-wegen-600 hover:underline">
                  Return to dashboard
                </a>
              </div>
            }
          />
        </Routes>
      </Suspense>
    </ErrorBoundary>
  )
}
